import React from 'react';
import { createPortal } from 'react-dom';

import {
  ADD_RECORD_CLOSE_LABEL,
  ADD_RECORD_TITLES,
} from 'constants/uiConstants';
import type { GUnit } from 'interfaces/IGlobalCatalogue';

import { AddProductKind, AddRecordValues } from './addRecord';
import AddRecordForm from './AddRecordForm';
import FlyoutHeader from './FlyoutHeader';
import { StepSelectorOption } from './stepConfig';
import useFlyoutPosition from './useFlyoutPosition';

interface AddRecordFlyoutProps {
  anchorRef: React.RefObject<HTMLElement>;
  kind: AddProductKind;
  /** The text typed in the picker search, pre-filled as the name. */
  initialName: string;
  gUnits: GUnit[];
  onClose: () => void;
  /** Called with the saved record, ready to be selected in the picker. */
  onCreated: (option: StepSelectorOption, values: AddRecordValues) => void;
}

/**
 * The Add form portaled beside the step picker (OP-2498). Closing without
 * saving sends nothing.
 */
const AddRecordFlyout: React.FC<AddRecordFlyoutProps> = ({
  anchorRef,
  kind,
  initialName,
  gUnits,
  onClose,
  onCreated,
}) => {
  const { position, portalContainer } = useFlyoutPosition(anchorRef);

  if (!position || !portalContainer) return null;

  return createPortal(
    <div
      style={
        {
          '--flyout-top': `${position.top}rem`,
          '--flyout-left': `${position.left}rem`,
          '--flyout-width': `${position.width}rem`,
          '--flyout-height': `${position.height}rem`,
        } as React.CSSProperties
      }
      tabIndex={-1}
      className="step-picker-flyout z-[1080] flex flex-col overflow-hidden rounded-l-none rounded-lg border-l bg-white outline-none"
      onKeyDown={(event) => {
        // Escape closes this form only, not the picker behind it.
        if (event.key === 'Escape') {
          event.stopPropagation();
          event.nativeEvent.stopImmediatePropagation();
          onClose();
        }
      }}
    >
      <FlyoutHeader
        title={ADD_RECORD_TITLES[kind]}
        closeLabel={ADD_RECORD_CLOSE_LABEL}
        onClose={onClose}
      />
      <AddRecordForm
        kind={kind}
        initialName={initialName}
        gUnits={gUnits}
        onCreated={onCreated}
      />
    </div>,
    portalContainer
  );
};

export default AddRecordFlyout;
